import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

function parseCsv(text: string): string[][] {
    const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
    const delimiter = [';', '\t', ',']
        .map((d) => [d, firstLine.split(d).length] as const)
        .sort((a, b) => b[1] - a[1])[0][0];
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (quoted) {
            if (char === '"' && text[i + 1] === '"') {
                cell += '"';
                i++;
            } else if (char === '"') {
                quoted = false;
            } else {
                cell += char;
            }
        } else if (char === '"') {
            quoted = true;
        } else if (char === delimiter) {
            row.push(cell);
            cell = '';
        } else if (char === '\n' || char === '\r') {
            if (char === '\r' && text[i + 1] === '\n') i++;
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
        } else {
            cell += char;
        }
    }
    if (cell !== '' || row.length > 0) {
        row.push(cell);
        rows.push(row);
    }
    return rows;
}

const isNumeric = (value: string) => /^-?[\d.]+(,\d+)?$/.test(value.trim());

function CsvDataTable({
    rows,
    columnCount
}: {
    rows: string[][];
    columnCount: number;
}) {
    const [query, setQuery] = useState('');
    const [header, ...body] = rows;
    const columns = Array.from({ length: columnCount }, (_, i) => i);
    const filtered = body.filter((row) =>
        row.some((cell) => cell.toLowerCase().includes(query.toLowerCase()))
    );
    return (
        <div className="flex flex-col gap-3 h-full">
            <div className="relative max-w-sm">
                <Search
                    size={14}
                    className="top-1/2 left-2.5 absolute text-muted-foreground -translate-y-1/2"
                />
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Søg i rækker"
                    className="py-1.5 pr-3 pl-8 border border-border rounded-md w-full text-sm"
                />
            </div>
            <div className="flex-1 border border-border rounded-lg overflow-auto">
                <table className="w-full text-sm">
                    <thead className="top-0 sticky bg-muted">
                        <tr>
                            {columns.map((c) => (
                                <th
                                    key={c}
                                    className={`px-4 py-2 font-medium whitespace-nowrap ${
                                        isNumeric(body[0]?.[c] ?? '')
                                            ? 'text-right'
                                            : 'text-left'
                                    }`}
                                >
                                    {header?.[c] ?? ''}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((row, r) => (
                            <tr
                                key={r}
                                className={r % 2 ? 'bg-muted/40' : undefined}
                            >
                                {columns.map((c) => (
                                    <td
                                        key={c}
                                        className={`px-4 py-2 whitespace-nowrap ${
                                            isNumeric(row[c] ?? '')
                                                ? 'text-right tabular-nums'
                                                : ''
                                        }`}
                                    >
                                        {row[c] ?? ''}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="text-muted-foreground text-xs">
                Viser {filtered.length} af {body.length} rækker
            </div>
        </div>
    );
}

export function CsvTablePreview({ url }: { url: string }) {
    const [text, setText] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setText(null);
        setFailed(false);
        fetch(url)
            .then((response) => {
                if (!response.ok) throw new Error(String(response.status));
                return response.text();
            })
            .then((value) => setText(value.replace(/^﻿/, '')))
            .catch(() => setFailed(true));
    }, [url]);

    const rows = useMemo(() => (text === null ? [] : parseCsv(text)), [text]);
    const columnCount = Math.max(1, ...rows.map((row) => row.length));

    if (failed) {
        return (
            <div className="p-4 text-muted-foreground text-sm">
                Preview not available. Use Download to open the file.
            </div>
        );
    }
    if (text === null) return null;

    return <CsvDataTable rows={rows} columnCount={columnCount} />;
}
